import type { ColumnDef, RowData, Table } from "@tanstack/vue-table";
import type { MaybeRef, Ref } from "vue";
import type { DataTableFilters } from "./filters";
import type { SetFilterEvent } from "./helpers";
import {
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useVueTable,
} from "@tanstack/vue-table";
import { ref, unref } from "vue";
import { defaultFilterFn, fuzzyFilter, valueUpdater } from "../helpers/utils";
import { useTableFilters } from "./filters";
import { tableEmitter } from "./helpers";

/**
 * Paging contract, discriminated by mode so contradictory states are not
 * expressible: an offset table has a total and derives pages from it, a
 * cursor table can only navigate to pages whose key is already stored, and
 * they never cross. The count of a cursor table is FOOTER TEXT - never the
 * source of navigation decisions.
 */
export type TablePaging =
  | { mode: "client" }
  | { mode: "offset"; total: Ref<number> }
  | { mode: "cursor"; knownPage: Ref<number>; exactTotal: Ref<number | null> };

declare module "@tanstack/vue-table" {
  interface TableState {
    loading: Ref<boolean>;
    paging: TablePaging;
  }

  // eslint-disable-next-line unused-imports/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    align?: "left" | "center" | "right";
    freeze?: "left" | "right";
    hide?: boolean;
    class?: string;
  }
}

interface TableOptions<T> {
  id?: string;
  data: MaybeRef<T[]>;
  columns: MaybeRef<ColumnDef<T>[]>;
  filters?: DataTableFilters;
  tableOptions?: Partial<TableOptions<T>>;
  loading?: Ref<boolean>;
  onRowClicked?: (row: T) => void;
}

export interface ClientTable<T> extends Table<T> {
  id: string;
  setFilter: (params: SetFilterEvent) => void;
  onRowClicked?: (row: T) => void;
}

export const useTable = <T,>(options: TableOptions<T>) => {
  const { columnFilters, globalFilter, pagination, rowSelection, sorting } =
    options.filters ?? useTableFilters();
  const loading = options.loading ?? ref(false);

  const columns = unref(options.columns).map((column) => ({
    ...column,
    filterFn: column.filterFn ?? defaultFilterFn,
    sortingFn: column.sortingFn ?? 'auto',
  }));

  const table = useVueTable<T>({
    ...options.tableOptions,
    get data() {
      const data = unref(options.data);
      return data;
    },
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),

    globalFilterFn: fuzzyFilter,
    getColumnCanGlobalFilter: (column) => {
      return column.columnDef.enableGlobalFilter ?? false;
    },

    state: {
      get pagination() {
        return pagination.value;
      },
      get sorting() {
        return sorting.value;
      },
      get columnFilters() {
        return columnFilters.value;
      },
      get rowSelection() {
        return rowSelection.value;
      },
      get globalFilter() {
        return globalFilter.value;
      },
      loading,
      paging: { mode: "client" },
    },

    onPaginationChange: (updaterOfValue) => valueUpdater(updaterOfValue, pagination),
    onSortingChange: (updaterOrValue) => valueUpdater(updaterOrValue, sorting),
    onColumnFiltersChange: (updaterOrValue) => valueUpdater(updaterOrValue, columnFilters),
    onRowSelectionChange: (updaterOrValue) => valueUpdater(updaterOrValue, rowSelection),
    onGlobalFilterChange: (updaterOrValue) => valueUpdater(updaterOrValue, globalFilter),
  });

  const id = options.id || Math.random().toString(36).substring(7);

  function setFilter(params: SetFilterEvent) {
    tableEmitter.emit("set-filter", params);
  }

  return {
    ...table,
    id,
    setFilter,
    onRowClicked: options.onRowClicked,
  };
};
